import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IFilterProForma } from '@shared/models/filters';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectFiltersCurrent } from '@state/filters/filters.selectors';
import { setUnsavedProFormaFiltersAction } from '@state/filters/filters.actions';

@UntilDestroy()
@Component({
  selector: 'app-filter-v2-financials',
  templateUrl: './filter-v2-financials.component.html',
  styleUrls: [
    './filter-v2-financials.component.scss',
    '../filters-v2.component.scss',
  ],
})
export class FilterV2FinancialsComponent implements OnInit {
  @Input() mobile = false;
  @ViewChild('trigger') menuTrigger!: MatMenuTrigger;
  @Output() applyFilters = new EventEmitter();

  protected isOpen = false;
  protected isActive = false;
  protected checkedCount = 0;

  protected capRate: number | null = null;
  protected grossYield: number | null = null;
  protected projectROI: number | null = null;

  constructor(private store: Store<AppState>) {}

  truncateNumber(number: number) {
    const multiplier = Math.pow(10, 2);
    return Math.floor(number * multiplier) / multiplier;
  }

  ngOnInit() {
    // for loading the first time
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.proForma) {
          if (filters.proForma.capitalizationRate?.greaterThanOrEqual) {
            this.capRate = this.truncateNumber(
              filters.proForma.capitalizationRate.greaterThanOrEqual * 100
            );
          }
          if (filters.proForma.grossYield?.greaterThanOrEqual) {
            this.grossYield = this.truncateNumber(
              filters.proForma.grossYield.greaterThanOrEqual * 100
            );
          }
          if (filters.proForma.projectRoi?.greaterThanOrEqual) {
            this.projectROI = this.truncateNumber(
              filters.proForma.projectRoi.greaterThanOrEqual * 100
            );
          }
        }
        this.checkIsActive();
        this.updateCheckedCount();
      });

    // for clearing the filters
    this.store
      .select(selectFiltersCurrent)
      .pipe(untilDestroyed(this))
      .subscribe((filters) => {
        const isProFormaEmpty =
          !filters?.proForma ||
          (!filters.proForma.capitalizationRate?.greaterThanOrEqual &&
            !filters.proForma.grossYield?.greaterThanOrEqual &&
            !filters.proForma.projectRoi?.greaterThanOrEqual);

        if (isProFormaEmpty) {
          this.capRate = null;
          this.grossYield = null;
          this.projectROI = null;
        }
        this.updateCheckedCount();
        this.checkIsActive();
      });
  }

  handleChanges() {
    let query: IFilterProForma = {};
    if (this.capRate !== null && this.capRate > 0) {
      query.capitalizationRate = { greaterThanOrEqual: this.capRate / 100 };
    }
    if (this.grossYield !== null && this.grossYield > 0) {
      query.grossYield = { greaterThanOrEqual: this.grossYield / 100 };
    }
    if (this.projectROI !== null && this.projectROI > 0) {
      query.projectRoi = { greaterThanOrEqual: this.projectROI / 100 };
    }
    this.updateCheckedCount();
    this.checkIsActive();
    this.store.dispatch(setUnsavedProFormaFiltersAction(query));
    if (!this.mobile) this.applyFilters.emit();
  }

  selectAll(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    input.select();
  }

  resetProForma() {
    this.capRate = null;
    this.grossYield = null;
    this.projectROI = null;
    this.updateCheckedCount();
    this.checkIsActive();
    this.store.dispatch(setUnsavedProFormaFiltersAction({}));
    if (!this.mobile) this.applyFilters.emit();
  }

  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  checkIsActive() {
    this.isActive =
      this.capRate !== null ||
      this.grossYield !== null ||
      this.projectROI !== null;
  }

  updateCheckedCount() {
    this.checkedCount = [this.capRate, this.grossYield, this.projectROI].filter(
      (value) => value !== null && value > 0
    ).length;
  }

  loadInitialValue() {
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        const isProFormaEmpty =
          !filters?.proForma ||
          (!filters.proForma.capitalizationRate?.greaterThanOrEqual &&
            !filters.proForma.grossYield?.greaterThanOrEqual &&
            !filters.proForma.projectRoi?.greaterThanOrEqual);

        if (!isProFormaEmpty && filters.proForma) {
          if (filters.proForma.capitalizationRate?.greaterThanOrEqual) {
            this.capRate = this.truncateNumber(
              filters.proForma.capitalizationRate.greaterThanOrEqual * 100
            );
          } else {
            this.capRate = null;
          }
          if (filters.proForma.grossYield?.greaterThanOrEqual) {
            this.grossYield = this.truncateNumber(
              filters.proForma.grossYield.greaterThanOrEqual * 100
            );
          } else {
            this.grossYield = null;
          }
          if (filters.proForma.projectRoi?.greaterThanOrEqual) {
            this.projectROI = this.truncateNumber(
              filters.proForma.projectRoi.greaterThanOrEqual * 100
            );
          } else {
            this.projectROI = null;
          }
          this.checkIsActive();
        } else {
          this.capRate = null;
          this.grossYield = null;
          this.projectROI = null;
          this.checkIsActive();
        }
      });
  }
}
